import { useEffect, useState } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faSpinner, faAward } from '@fortawesome/free-solid-svg-icons';
import { useAuth } from '../../contexts/AuthContext';
import { authFetch, formatDate } from '../../utils/http';
import StatementModal from '../results/StatementModal';

interface Summary {
  id: string;
  statementNumber: string;
  learnershipTitle: string;
  overallResult: string;
  issuedAt: string;
}

/** Statements of results issued to the signed-in learner. */
export default function MyStatementsPanel() {
  const { accessToken } = useAuth();
  const [items, setItems] = useState<Summary[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState('');
  const [openId, setOpenId] = useState<string | null>(null);

  useEffect(() => {
    (async () => {
      const res = await authFetch<Summary[]>(accessToken, '/api/marks/my-statements');
      if (res.ok && res.data) setItems(res.data);
      else setError(res.message || 'Failed to load your statements.');
      setIsLoading(false);
    })();
  }, [accessToken]);

  if (isLoading) {
    return (
      <div className="flex justify-center py-12">
        <FontAwesomeIcon icon={faSpinner} className="h-8 w-8 text-brand-600 animate-spin" />
      </div>
    );
  }

  return (
    <div className="space-y-3">
      {error && <p className="text-sm text-red-700 bg-red-50 border border-red-200 rounded-lg p-3">{error}</p>}

      {items.length === 0 && !error && (
        <div className="p-8 text-center">
          <FontAwesomeIcon icon={faAward} className="h-12 w-12 text-brand-300 mb-4" />
          <p className="text-brand-900/60">No statements of results have been issued to you yet.</p>
        </div>
      )}

      {items.map((s) => (
        <button
          key={s.id}
          onClick={() => setOpenId(s.id)}
          className="w-full text-left border border-canvas-300 rounded-2xl p-5 hover:bg-canvas-50 transition-colors flex items-center justify-between gap-3"
        >
          <div className="min-w-0">
            <p className="font-bold text-brand-950 truncate">{s.learnershipTitle}</p>
            <p className="text-xs text-brand-900/50 mt-0.5">
              {s.statementNumber} · issued {formatDate(s.issuedAt)}
            </p>
          </div>
          <span
            className={`px-2.5 py-1 rounded-full text-xs font-semibold flex-shrink-0 ${
              s.overallResult === 'Competent' ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'
            }`}
          >
            {s.overallResult}
          </span>
        </button>
      ))}

      {openId && <StatementModal statementId={openId} onClose={() => setOpenId(null)} />}
    </div>
  );
}
